import { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Fetches a single document from Firestore.
 * Returns { data, loading, error } — falls back to `defaultData` while loading or on error.
 *
 * Firestore structure:
 *   Collection "pages" → Documents: "home", "about", "skills", "contact"
 *   Collection "experiences" → Documents with order field
 */
export function usePageData(pageId, defaultData) {
  const [data, setData] = useState(defaultData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        const docRef = doc(db, 'pages', pageId);
        const docSnap = await getDoc(docRef);

        if (!cancelled) {
          if (docSnap.exists()) {
            setData({ ...defaultData, ...docSnap.data() });
          }
          // If doc doesn't exist, keep defaultData — no error
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn(`Firestore: could not fetch "pages/${pageId}":`, err.message);
          setError(err);
          setLoading(false);
        }
      }
    }

    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  return { data, loading, error };
}

/**
 * Fetches all documents from the "experiences" collection, ordered by `order` field.
 */
export function useExperiences(defaultData) {
  const [data, setData] = useState(defaultData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        const q = query(collection(db, 'experiences'), orderBy('order', 'asc'));
        const snapshot = await getDocs(q);

        if (!cancelled) {
          if (!snapshot.empty) {
            const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setData(docs);
          }
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Firestore: could not fetch "experiences":', err.message);
          setError(err);
          setLoading(false);
        }
      }
    }

    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading, error };
}
